const CONVEX_PREFIX_PATTERN = /^\[CONVEX[^\]]+\]\s*/i;
const REQUEST_ID_PATTERN = /^\[Request ID:\s*[^\]]+\]\s*/i;
const SERVER_ERROR_PREFIX_PATTERN = /^Server Error\s*Uncaught\s*(?:Error|ConvexError):\s*/i;
const INLINE_STACK_PATTERN = /\s+at\s+[A-Za-z0-9_$<>\.]+\s*\([^)]*\)[\s\S]*$/;
const CLIENT_SUFFIX_PATTERN = /\s+Called by client[\s\S]*$/i;
const AUTHORIZATION_ERROR_PATTERN =
  /\b(unauthorized|forbidden|access required|permission denied|not authenticated)\b/i;
const ARGUMENT_SIZE_ERROR_PATTERN = /node actions arguments size is too large/i;

type UserFacingErrorOptions = {
  unauthorizedMessage?: string;
  oversizeMessage?: string;
};

function getRawMessage(error: unknown): string | null {
  if (typeof error === "string") return error;

  if (error instanceof Error) {
    return error.message || null;
  }

  if (typeof error === "object" && error !== null && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string") return message;
  }

  return null;
}

function sanitizeConvexErrorMessage(message: string): string {
  const sanitized = message
    .replace(CONVEX_PREFIX_PATTERN, "")
    .replace(REQUEST_ID_PATTERN, "")
    .replace(SERVER_ERROR_PREFIX_PATTERN, "")
    .replace(/^Error:\s*/i, "")
    .replace(CLIENT_SUFFIX_PATTERN, "")
    .replace(INLINE_STACK_PATTERN, "");

  return (sanitized.split("\n")[0] ?? "").trim();
}

export function toUserFacingErrorMessage(
  error: unknown,
  defaultMessage: string,
  options?: UserFacingErrorOptions,
): string {
  const rawMessage = getRawMessage(error);
  if (!rawMessage) return defaultMessage;

  const normalizedMessage = sanitizeConvexErrorMessage(rawMessage);

  if (
    AUTHORIZATION_ERROR_PATTERN.test(rawMessage) ||
    AUTHORIZATION_ERROR_PATTERN.test(normalizedMessage)
  ) {
    return (
      options?.unauthorizedMessage ??
      "You don't have permission to perform this action. Contact an administrator."
    );
  }

  if (
    ARGUMENT_SIZE_ERROR_PATTERN.test(rawMessage) ||
    ARGUMENT_SIZE_ERROR_PATTERN.test(normalizedMessage)
  ) {
    return options?.oversizeMessage ?? "The selected file is too large. Please choose a smaller file and try again.";
  }

  return normalizedMessage || defaultMessage;
}
